import { createClient } from '@supabase/supabase-js'
import emailService from './emailService'

// Project credentials - auto-injected when connected
const SUPABASE_URL = 'https://wkscjmjdllqcgwqmwjfe.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Indrc2NqbWpkbGxxY2d3cW13amZlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzU5MTM4NDQsImV4cCI6MjA1MTQ4OTg0NH0.FGjCAjfP1pjAfhfJFVEuDvWP_Lq7VdYjZ8mJZB8yW4Q'

// Check if we have valid credentials
const hasValidCredentials = SUPABASE_URL !== 'https://your-project-id.supabase.co' && 
                           SUPABASE_ANON_KEY !== 'your-anon-key' && 
                           SUPABASE_URL && SUPABASE_ANON_KEY

let supabase = null

if (hasValidCredentials) {
  try {
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true
      }
    })
    console.log('✅ Supabase connected successfully')
  } catch (error) {
    console.error('❌ Supabase connection failed:', error)
  }
} else {
  console.warn('⚠️ Supabase credentials not configured, using localStorage fallback')
}

// Export the client
export { supabase }

// Enhanced fallback functions for when Supabase is not available
const localStorageFallback = {
  // Cities management
  async getCities() {
    const cities = localStorage.getItem('cityshot_cities')
    return cities ? JSON.parse(cities) : []
  },

  async addCity(cityData) {
    const cities = await this.getCities()
    const newCity = {
      id: `city-${Date.now()}`,
      ...cityData,
      created_at: new Date().toISOString(),
      email_verified: false,
      onboarded: false
    }
    cities.push(newCity)
    localStorage.setItem('cityshot_cities', JSON.stringify(cities))
    return newCity
  },

  async updateCity(cityId, updates) {
    const cities = await this.getCities()
    const index = cities.findIndex(city => city.id === cityId)
    if (index !== -1) {
      cities[index] = { ...cities[index], ...updates }
      localStorage.setItem('cityshot_cities', JSON.stringify(cities))
      return cities[index]
    }
    return null
  },

  async deleteCity(cityId) {
    const cities = await this.getCities()
    const filteredCities = cities.filter(city => city.id !== cityId)
    localStorage.setItem('cityshot_cities', JSON.stringify(filteredCities))
    return true
  },

  // Configurations management
  async getConfig(cityId) {
    const config = localStorage.getItem(`cityshot_config_${cityId}`)
    return config ? JSON.parse(config) : null
  },

  async saveConfig(cityId, config) {
    const configWithTimestamp = {
      ...config,
      updated_at: new Date().toISOString()
    }
    localStorage.setItem(`cityshot_config_${cityId}`, JSON.stringify(configWithTimestamp))
    return configWithTimestamp
  },

  // Magic link management with REAL EMAIL INTEGRATION
  async createMagicLink(email, purpose = 'login') {
    const token = btoa(Math.random().toString()).substring(0, 32)
    const expiresAt = new Date(Date.now() + 60 * 60 * 1000) // 1 hour
    
    const magicLink = {
      email,
      token,
      purpose,
      expires_at: expiresAt.toISOString(),
      used: false,
      created_at: new Date().toISOString()
    }
    
    // Store magic link
    const magicLinks = JSON.parse(localStorage.getItem('cityshot_magic_links') || '[]')
    magicLinks.push(magicLink)
    localStorage.setItem('cityshot_magic_links', JSON.stringify(magicLinks))
    
    // 🚀 SEND REAL EMAIL using email service
    try {
      const cities = await this.getCities()
      const city = cities.find(c => c.email === email)
      const cityName = city?.city_name || null

      let emailResult
      if (purpose === 'verify_email') {
        emailResult = await emailService.sendVerificationEmail(email, token, cityName)
      } else {
        emailResult = await emailService.sendMagicLink(email, token, cityName)
      }

      if (emailResult.success) {
        console.log('✅ Real email sent successfully:', emailResult)
      } else {
        console.warn('⚠️ Email service failed, falling back to console logging')
        // Fallback to console logging
        console.log(`📧 Magic link for ${email}`)
        console.log(`🔗 Magic Link URL: ${window.location.origin}/#/?token=${token}`)
      }
    } catch (error) {
      console.error('❌ Email service error, falling back to console logging:', error)
      // Fallback to console logging
      console.log(`📧 Magic link for ${email}`)
      console.log(`🔗 Magic Link URL: ${window.location.origin}/#/?token=${token}`)
    }
    
    return { token, expires_at: expiresAt.toISOString() }
  },

  async validateMagicLink(token) {
    const magicLinks = JSON.parse(localStorage.getItem('cityshot_magic_links') || '[]')
    const link = magicLinks.find(l => 
      l.token === token && 
      !l.used && 
      new Date(l.expires_at) > new Date()
    )
    
    if (!link) {
      return { valid: false, email: null, purpose: null, city_id: null }
    }
    
    // Mark as used
    link.used = true
    link.used_at = new Date().toISOString()
    localStorage.setItem('cityshot_magic_links', JSON.stringify(magicLinks))
    
    // Find city by email
    const cities = await this.getCities()
    const city = cities.find(c => c.email === link.email)
    
    return {
      valid: true,
      email: link.email,
      purpose: link.purpose,
      city_id: city?.id || null
    }
  },

  // FIXED: TESTFREE makes product 100% free
  async calculateCouponDiscount(couponCode, originalPrice) {
    const validCoupons = {
      'WELCOME50': { type: 'percentage', value: 50 },
      'EARLY2024': { type: 'percentage', value: 100 },
      'TESTFREE': { type: 'percentage', value: 100 }, // FIXED: 100% off (completely free)
      'LAUNCH': { type: 'percentage', value: 75 }
    }
    
    const coupon = validCoupons[couponCode.toUpperCase()]
    if (!coupon) {
      return {
        valid: false,
        discount_amount: 0,
        final_price: originalPrice,
        message: 'Invalid coupon code'
      }
    }
    
    let discountAmount
    if (coupon.type === 'percentage') {
      discountAmount = (originalPrice * coupon.value / 100)
    } else {
      discountAmount = coupon.value // Fixed amount in EUR
    }
    
    // Ensure discount doesn't exceed original price
    discountAmount = Math.min(discountAmount, originalPrice)
    const finalPrice = Math.max(0, originalPrice - discountAmount)
    
    return {
      valid: true,
      discount_amount: discountAmount,
      final_price: finalPrice,
      message: 'Coupon applied successfully'
    }
  },

  // Authentication - passwordless magic link only with REAL EMAILS
  async sendMagicLink(email) {
    // Check if super admin
    if (email === 'cityshot@clickbooster.app') {
      const token = await this.createMagicLink(email, 'login')
      // Auto-login for demo
      setTimeout(() => {
        window.location.href = `${window.location.origin}/#/?token=${token.token}`
      }, 1000)
      return { success: true, message: 'Magic link sent' }
    }

    // Check if city exists
    const cities = await this.getCities()
    const city = cities.find(c => c.email === email)
    
    if (city) {
      const token = await this.createMagicLink(email, 'login')
      // Auto-login for demo accounts
      if (email === 'musterstadt@clickbooster.app') {
        setTimeout(() => {
          window.location.href = `${window.location.origin}/#/?token=${token.token}`
        }, 1000)
      }
      return { success: true, message: 'Magic link sent to your email' }
    }

    return { success: false, error: 'No account found with this email address' }
  },

  async signOut() {
    return { error: null }
  }
}

// Database operations with enhanced functionality
export const dbOperations = {
  // Cities
  async getCities() {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('cities_auth_v2')
          .select('*')
          .order('created_at', { ascending: false })
        
        if (error) throw error
        return data || []
      } catch (error) {
        console.error('Supabase getCities error:', error)
        return localStorageFallback.getCities()
      }
    }
    return localStorageFallback.getCities()
  },

  async addCity(cityData) {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('cities_auth_v2')
          .insert([cityData])
          .select()
          .single()
        
        if (error) throw error

        // 🎉 Send welcome email for new cities
        try {
          await emailService.sendWelcomeEmail(cityData.email, cityData.city_name)
        } catch (emailError) {
          console.warn('Failed to send welcome email:', emailError)
          // Don't fail city creation if welcome email fails
        }

        return data
      } catch (error) {
        console.error('Supabase addCity error:', error)
        return localStorageFallback.addCity(cityData)
      }
    }
    return localStorageFallback.addCity(cityData)
  },

  async updateCity(cityId, updates) {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('cities_auth_v2')
          .update(updates)
          .eq('id', cityId)
          .select()
          .single()
        
        if (error) throw error
        return data
      } catch (error) {
        console.error('Supabase updateCity error:', error)
        return localStorageFallback.updateCity(cityId, updates)
      }
    }
    return localStorageFallback.updateCity(cityId, updates)
  },

  async deleteCity(cityId) {
    if (supabase) {
      try {
        const { error } = await supabase
          .from('cities_auth_v2')
          .delete()
          .eq('id', cityId)
        
        if (error) throw error
        return true
      } catch (error) {
        console.error('Supabase deleteCity error:', error)
        return localStorageFallback.deleteCity(cityId)
      }
    }
    return localStorageFallback.deleteCity(cityId)
  },

  // Configurations
  async getConfig(cityId) {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('city_configurations_v2')
          .select('*')
          .eq('city_id', cityId)
          .single()
        
        if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows returned
        return data?.configuration || null
      } catch (error) {
        console.error('Supabase getConfig error:', error)
        return localStorageFallback.getConfig(cityId)
      }
    }
    return localStorageFallback.getConfig(cityId)
  },

  async saveConfig(cityId, config) {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('city_configurations_v2')
          .upsert({
            city_id: cityId,
            configuration: config,
            updated_at: new Date().toISOString()
          })
          .select()
          .single()
        
        if (error) throw error
        return data.configuration
      } catch (error) {
        console.error('Supabase saveConfig error:', error)
        return localStorageFallback.saveConfig(cityId, config)
      }
    }
    return localStorageFallback.saveConfig(cityId, config)
  },

  // Magic link management with REAL EMAIL INTEGRATION
  async createMagicLink(email, purpose = 'login') {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .rpc('create_magic_link_v2', {
            p_email: email,
            p_purpose: purpose,
            p_expires_minutes: 60
          })
        
        if (error) throw error
        
        // 🚀 Send real email using email service
        try {
          const cities = await this.getCities()
          const city = cities.find(c => c.email === email)
          const cityName = city?.city_name || null

          let emailResult
          if (purpose === 'verify_email') {
            emailResult = await emailService.sendVerificationEmail(email, data, cityName)
          } else {
            emailResult = await emailService.sendMagicLink(email, data, cityName)
          }

          if (!emailResult.success && !emailResult.fallback) {
            console.warn('⚠️ Email service failed, falling back to console logging')
            console.log(`📧 Magic link token for ${email}: ${data}`)
            console.log(`🔗 Magic Link URL: ${window.location.origin}/#/?token=${data}`)
          }
        } catch (emailError) {
          console.error('❌ Email service error, falling back to console logging:', emailError)
          console.log(`📧 Magic link token for ${email}: ${data}`)
          console.log(`🔗 Magic Link URL: ${window.location.origin}/#/?token=${data}`)
        }
        
        return { token: data, expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString() }
      } catch (error) {
        console.error('Supabase createMagicLink error:', error)
        return localStorageFallback.createMagicLink(email, purpose)
      }
    }
    return localStorageFallback.createMagicLink(email, purpose)
  },

  async validateMagicLink(token) {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .rpc('validate_magic_link_v2', { p_token: token })
          .single()
        
        if (error) throw error
        return data
      } catch (error) {
        console.error('Supabase validateMagicLink error:', error)
        return localStorageFallback.validateMagicLink(token)
      }
    }
    return localStorageFallback.validateMagicLink(token)
  },

  // FIXED: TESTFREE coupon management - now makes products 100% free
  async calculateCouponDiscount(couponCode, originalPrice) {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .rpc('calculate_coupon_discount_v2', {
            p_coupon_code: couponCode,
            p_original_price: originalPrice
          })
          .single()
        
        if (error) throw error
        return data
      } catch (error) {
        console.error('Supabase calculateCouponDiscount error:', error)
        return localStorageFallback.calculateCouponDiscount(couponCode, originalPrice)
      }
    }
    return localStorageFallback.calculateCouponDiscount(couponCode, originalPrice)
  },

  async useCoupon(couponCode) {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .rpc('use_coupon_v2', { p_coupon_code: couponCode })
        
        if (error) throw error
        return data
      } catch (error) {
        console.error('Supabase useCoupon error:', error)
        return true // Fallback to success
      }
    }
    return true // Local storage doesn't track usage
  },

  // Magic link authentication with REAL EMAIL INTEGRATION
  async sendMagicLink(email) {
    if (supabase) {
      try {
        const result = await this.createMagicLink(email, 'login')
        
        if (result.token) {
          // For demo accounts, auto-redirect after a moment
          if (email === 'cityshot@clickbooster.app') {
            setTimeout(() => {
              window.location.href = `${window.location.origin}/#/?token=${result.token}`
            }, 2000)
          } else if (email === 'musterstadt@clickbooster.app') {
            setTimeout(() => {
              window.location.href = `${window.location.origin}/#/?token=${result.token}`
            }, 2000)
          }
          
          return { success: true, message: 'Magic link sent successfully' }
        }
        
        return { success: false, error: 'Failed to create magic link' }
      } catch (error) {
        console.error('Magic link error:', error)
        return localStorageFallback.sendMagicLink(email)
      }
    }
    return localStorageFallback.sendMagicLink(email)
  },

  async signOut() {
    if (supabase) {
      try {
        const { error } = await supabase.auth.signOut()
        return { error }
      } catch (error) {
        console.error('Supabase signOut error:', error)
        return { error: null }
      }
    }
    return localStorageFallback.signOut()
  },

  // Email verification
  async verifyEmail(token) {
    const result = await this.validateMagicLink(token)
    if (result.valid && result.city_id) {
      await this.updateCity(result.city_id, { email_verified: true })
    }
    return result
  }
}

// Helper function to check if we're connected to Supabase
export const isSupabaseConnected = () => {
  return supabase !== null
}

// Export for legacy compatibility
export default supabase